import { InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type Marker, MarkerClusterer } from "@googlemaps/markerclusterer";
import { LocationMarker } from "./LocationMarker";
import { Poi } from "../Map_view";
import { Button, ButtonTypes } from "../../../../../../components/ui/Button";
import { LocationsModel } from "../Map_model";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import "./_locationMarker.scss";
import { UiSizes } from "@flightpath/coreui/dist/enums";
export type ClusteredTreeMarkersProps = {
  locations: Poi[];
};

/**
 * The ClusteredTreeMarkers component is responsible for integrating the
 * markers with the markerclusterer.
 */
export const ClusteredLocationMarkers = ({ locations }: ClusteredTreeMarkersProps) => {
  const organisationId = useCurrentOrganisationId();
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [selectedLocationKey, setSelectedLocationKey] = useState<number | null>(null);
  const [model] = useState(() => new LocationsModel(organisationId));

  const selectedLocation = useMemo(
    () => (locations && selectedLocationKey ? locations.find(t => t.key === selectedLocationKey)! : null),
    [locations, selectedLocationKey]
  );

  // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap();
  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: number) => {
    setMarkers(markers => {
      if ((marker && markers[key]) || (!marker && !markers[key])) return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedLocationKey(null);
  }, []);

  const handleMarkerClick = useCallback((location: Poi) => {
    setSelectedLocationKey(location.key);
  }, []);

  return (
    <>
      {locations.map(location => (
        <LocationMarker
          key={location.key}
          location={location}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedLocationKey && (
        <InfoWindow anchor={markers[selectedLocationKey]}>
          <div className="d-flex justify-content-between align-items-start pl-2 py-2">
            <div style={{ minWidth: "250px", marginRight: "60px" }}>
              <h1 className="mb-0 mt-1 font-weight-bold">{selectedLocation?.name}</h1>
              <p>{`${selectedLocation.streetAddress} ${selectedLocation.city} ${selectedLocation.country}`}</p>
              <p className="mb-1">
                <strong>Total Project Processes Count: </strong> {selectedLocation.totalProjectProcesses}
              </p>
              <p className="mb-1">
                <strong>Project Names: </strong>
                {selectedLocation.projectNames ? selectedLocation.projectNames.split("|").join(", ") : "N/A"}
              </p>
              <p className="mb-1">
                <strong>Total Impacts Count: </strong>
                {selectedLocation.totalImpacts}
              </p>
              <p className="mb-0">
                <strong>Total Projects Count: </strong> {selectedLocation.totalProjects}
              </p>
            </div>

            <Button
              type={ButtonTypes.OUTLINE_PRIMARY}
              size={UiSizes.SM}
              onClick={handleInfoWindowClose}
              style={{
                border: "none",
                background: "transparent",
                fontSize: "30px",
                lineHeight: "1",
                cursor: "pointer",
                opacity: ".6",
                padding: "0px 0px 0px 10px",
                alignSelf: "flex-start"
              }}
              aria-label="Close"
            >
              ×
            </Button>
          </div>
          <div className="d-flex">
            <Button className="w-100 my-2 ml-1" onClick={() => model.showMapReportingModal(selectedLocation.key)}>
              Learn more
            </Button>
          </div>
        </InfoWindow>
      )}
    </>
  );
};
